import React, { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import FormInputWithPrecisionDisplay from 'components/PriceUnitField/components/FormInputWithPrecisionDisplay';
import { Product } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';

interface InputWithPreferenceBlurProps {
  fieldName: string;
  label?: string;
  id?: string;
  isRequired?: boolean;
  disabled?: boolean;
  inputSuffix?: string;
  addonAfter?: React.ReactNode;
  onControlledBlur: (value: any, ...args: any[]) => void;
  /** Renders blank instead of "0.00" for a null/empty value. */
  allowEmptyDisplay?: boolean;
}

/**
 * Wrapper around FormInputWithPrecisionDisplay that automatically triggers
 * onBlur when preferences are loaded programmatically.
 */
const InputWithPreferenceBlur: React.FC<InputWithPreferenceBlurProps> = ({
  fieldName,
  label,
  id,
  isRequired,
  disabled,
  inputSuffix,
  addonAfter,
  onControlledBlur,
  allowEmptyDisplay = false
}) => {
  const { values } = useFormikContext<Product & { _preferenceData?: any }>();
  const previousPreferenceData = useRef(values._preferenceData);
  const onControlledBlurRef = useRef(onControlledBlur);

  // Keep the ref up to date with the latest callback
  useEffect(() => {
    onControlledBlurRef.current = onControlledBlur;
  }, [onControlledBlur]);

  useEffect(() => {
    // Detect when _preferenceData changes from undefined to defined (preference just loaded)
    if (
      !previousPreferenceData.current &&
      values._preferenceData &&
      !disabled
    ) {
      // Wait for Formik state to settle, then trigger blur
      const timeoutId = setTimeout(() => {
        const currentValue = (values as any)[fieldName];
        if (currentValue && onControlledBlurRef.current) {
          // Call the blur handler directly with the current value
          onControlledBlurRef.current(currentValue);
        }
      }, 100);

      // Update ref before return
      previousPreferenceData.current = values._preferenceData;

      // Cleanup: clear timeout if component unmounts or effect re-runs
      return () => clearTimeout(timeoutId);
    }

    previousPreferenceData.current = values._preferenceData;
    return undefined;
    // A disabled field is a mode that isn't the one the loaded preference
    // restored — recalculating from it here would overwrite the value the
    // active field's own restore just set (e.g. Rate per Acre's calculation
    // clobbering a restored Total Product value).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values._preferenceData, fieldName, disabled]);

  return (
    <FormInputWithPrecisionDisplay
      fieldName={fieldName}
      label={label}
      id={id}
      isRequired={isRequired}
      disabled={disabled}
      inputSuffix={inputSuffix}
      addonAfter={addonAfter}
      onControlledBlur={onControlledBlur}
      allowEmptyDisplay={allowEmptyDisplay}
    />
  );
};

export default InputWithPreferenceBlur;
